import {
    PUBLIC_CONFIG_GAME_LAUNCHER_URL,
    PUBLIC_CONFIG_GAME_LAUNCHER_URL_TEST,
    PUBLIC_KEYCLOAK_CLIENT_ID,
    PUBLIC_KEYCLOAK_CLIENT_ID_TEST,
    PUBLIC_KEYCLOAK_COOKIE_DOMAIN,
    PUBLIC_KEYCLOAK_COOKIE_DOMAIN_TEST,
    PUBLIC_KEYCLOAK_ES_COOKIE_DOMAIN,
    PUBLIC_KEYCLOAK_ES_COOKIE_DOMAIN_TEST,
    PUBLIC_KEYCLOAK_URL,
    PUBLIC_KEYCLOAK_URL_ES,
    PUBLIC_KEYCLOAK_URL_ES_TEST,
    PUBLIC_KEYCLOAK_URL_TEST,
    PUBLIC_NO_TRAILING_SLASH_URLS_REGEX,
    PUBLIC_NO_TRAILING_SLASH_URLS_REGEX_TEST,
    PUBLIC_REGILY_ONTARIO_SCRIPT,
    PUBLIC_REGILY_ONTARIO_SCRIPT_TEST
} from '$env/static/public';
import type { TDeviceDetails } from '../device/types';
import { isTest } from '../environment';
import type { Gatekeeper } from '../gatekeeper/gatekeeper';
import type { TCmsPageModuleNode } from '../losService/types';
import { MARKET_SLUGS } from '../market/slugs';
import type { TMarket } from '../market/types';
import type { TActiveRoute } from '../routing/types';
import { LEGACY_MFES, MFE_NAMES } from './constants';
import type { TBlockContext, TBlock } from './types';
import { encodeParams, safeStringify } from './utils';

export const getBlock = (
    { moduleName: module, props: params, mfe, variant }: TCmsPageModuleNode,
    context: TBlockContext,
    markup: string = '',
    styles: string[] = [],
    scriptSrc: string = '',
    hydrate: boolean = true,
    ssr: boolean = false
): TBlock => {
    const isLegacy = mfe && LEGACY_MFES.includes(mfe);
    const block: TBlock = {
        mfe: mfe || MFE_NAMES.DEFAULT,
        module,
        payload: '',
        legacyParams: '',
        scriptSrc,
        styles: {
            decoded: styles,
            encoded: ''
        },
        markup,
        hydrate: (ssr && hydrate) || !ssr,
        variant
    };

    if (isLegacy) {
        block.legacyParams = Object.keys(params)
            .map((key: string) => {
                const value = params[key];
                return `${key}="${value}"`;
            })
            .join(' ');
        block.hydrate = true;
        block.module = params.component;
    }

    try {
        if (!isLegacy) {
            block.payload = encodeParams({ ...context, module, params });
        }
    } catch (e) {
        console.error(e);
    }
    try {
        block.styles.encoded = btoa(safeStringify(styles));
    } catch (e) {
        console.error(e);
    }
    return block;
};

// Function below can run both in the browser and server, hence the gatekeeper parameter can be a string or an instance of Gatekeeper
export const getBlockContext = async (
    market: TMarket,
    deviceDetails: TDeviceDetails,
    route: TActiveRoute,
    url: URL | null,
    countryCodeByIp: string | null,
    operationalNavigationSlugs: Record<string, string>,
    gatekeeper: Gatekeeper
): Promise<TBlockContext> => {
    let pageNumber = '';
    const isCasumoTestEnv = isTest(url?.hostname || '');

    if (url?.pathname.includes('/page/')) {
        const match = url.pathname.match(/\/page\/(\d+)\//);

        pageNumber = match ? match[1] : '';
    }

    const casumoTestConfig = {
        gameLauncherUrl: PUBLIC_CONFIG_GAME_LAUNCHER_URL_TEST,
        keycloakUrl:
            market.slug === MARKET_SLUGS.ES
                ? PUBLIC_KEYCLOAK_URL_ES_TEST
                : PUBLIC_KEYCLOAK_URL_TEST,
        keycloakClientId: PUBLIC_KEYCLOAK_CLIENT_ID_TEST,
        keycloakCookieDomain:
            market.slug === MARKET_SLUGS.ES
                ? PUBLIC_KEYCLOAK_ES_COOKIE_DOMAIN_TEST
                : PUBLIC_KEYCLOAK_COOKIE_DOMAIN_TEST,
        noTrailingSlashUrlsRegex: PUBLIC_NO_TRAILING_SLASH_URLS_REGEX_TEST,
        regilyOntarioScript: PUBLIC_REGILY_ONTARIO_SCRIPT_TEST,
        countryCodeByIp: countryCodeByIp || '__'
    };

    const casumoConfig = {
        gameLauncherUrl: PUBLIC_CONFIG_GAME_LAUNCHER_URL,
        keycloakUrl: market.slug === MARKET_SLUGS.ES ? PUBLIC_KEYCLOAK_URL_ES : PUBLIC_KEYCLOAK_URL,
        keycloakClientId: PUBLIC_KEYCLOAK_CLIENT_ID,
        keycloakCookieDomain:
            market.slug === MARKET_SLUGS.ES
                ? PUBLIC_KEYCLOAK_ES_COOKIE_DOMAIN
                : PUBLIC_KEYCLOAK_COOKIE_DOMAIN,
        noTrailingSlashUrlsRegex: PUBLIC_NO_TRAILING_SLASH_URLS_REGEX,
        regilyOntarioScript: PUBLIC_REGILY_ONTARIO_SCRIPT,
        countryCodeByIp: countryCodeByIp || '__'
    };

    return {
        serverGatekeeper: await gatekeeper.getGatekeeperSnapshot(),
        globals: {
            market: market.slug,
            config: isCasumoTestEnv ? casumoTestConfig : casumoConfig,
            device: {
                ...deviceDetails,
                device: deviceDetails.isMobile ? 'mobile' : 'desktop'
            },
            route: {
                origin: route?.origin,
                pathname: route?.url,
                params: {
                    ...route?.params,
                    pageNumber
                },
                search: url?.search || ''
            },
            navigation: {
                operationalSlugs: operationalNavigationSlugs
            }
        }
    };
};
